/**
 * HocuspocusWebsocketProvider — a drop-in replacement for y-websocket's
 * `WebsocketProvider`, backed by `@hocuspocus/provider`'s `HocuspocusProvider`.
 *
 * Why this exists
 * ───────────────
 * The collaboration server migrated from a custom y-websocket server to
 * Hocuspocus. The two speak DIFFERENT wire protocols: y-websocket frames each
 * message as `[messageSync, …]` with the room name only in the URL, while
 * Hocuspocus expects a `varString` document name at the start of every message.
 * A vanilla `WebsocketProvider` therefore CANNOT talk to Hocuspocus (the server
 * crashes parsing the first sync message).
 *
 * Rather than rewrite the ~50 call sites that construct/consume a
 * `WebsocketProvider` (directly or via `WSClient`), this class exposes the exact
 * surface those call sites rely on — constructor signature, `awareness`, `doc`,
 * `synced`, `wsconnected`/`wsconnecting`, `shouldConnect`, `connect()`,
 * `disconnect()`, `destroy()`, and the `sync`/`synced`/`status`/
 * `connection-close`/`connection-error` events — while delegating the actual
 * transport to a `HocuspocusProvider`.
 *
 * Native behaviour gained for free from Hocuspocus:
 *   • automatic reconnection with exponential backoff (replaces
 *     wsReconnectCoordinator);
 *   • token (re)evaluation on every (re)connect via a token function (replaces
 *     wsTokenRefresher's URL-param rotation);
 *   • multiplexing-ready protocol framing.
 *
 * Event name mapping (HocuspocusProvider → WebsocketProvider):
 *   synced {state}     → 'sync'(state) and 'synced'(state)
 *   status {status}    → 'status'({status})
 *   close  {event}     → 'connection-close'(event)
 *   disconnect {event} → 'connection-error'(event)
 */

import { HocuspocusProvider, WebSocketStatus } from '@hocuspocus/provider';
import { Awareness } from 'y-protocols/awareness';
import type * as Y from 'yjs';

export interface HocuspocusWebsocketProviderOptions {
  /** Forwarded — whether to connect immediately. Default: true. */
  connect?: boolean;
  /** Awareness instance. Created automatically if omitted. */
  awareness?: Awareness;
  /** Static URL params. Only `token` is used (sent via the auth handshake). */
  params?: Record<string, string>;
  /**
   * Live token provider — preferred over `params.token`. Called on every
   * (re)connect so a rotated auth token is picked up without reconnect glue.
   */
  tokenProvider?: () => string | undefined;
  /** WebSocket implementation (Node test/SSR). Defaults to global WebSocket. */
  WebSocketPolyfill?: unknown;
  /** Accepted for signature parity; Hocuspocus manages resync internally. */
  resyncInterval?: number;
  /** Accepted for signature parity; Hocuspocus manages backoff internally. */
  maxBackoffTime?: number;
  /** Accepted for signature parity (BroadcastChannel not used here). */
  disableBc?: boolean;
}

type Listener = (...args: unknown[]) => void;

export class HocuspocusWebsocketProvider {
  readonly serverUrl: string;
  readonly roomname: string;
  readonly doc: Y.Doc;
  readonly awareness: Awareness;
  params: Record<string, string>;
  shouldConnect: boolean;

  private readonly hp: HocuspocusProvider;
  private readonly listeners = new Map<string, Set<Listener>>();
  private _status: WebSocketStatus = WebSocketStatus.Connecting;
  private _destroyed = false;

  /**
   * y-websocket compat: returns the full connection URL (serverUrl + roomname).
   * Debug tooling uses this to display the current connection endpoint.
   */
  get url(): string {
    return `${this.serverUrl}/${this.roomname}`;
  }

  constructor(
    serverUrl: string,
    roomname: string,
    doc: Y.Doc,
    opts: HocuspocusWebsocketProviderOptions = {},
  ) {
    // Match y-websocket: strip trailing slashes from the base URL.
    let url = serverUrl;
    while (url.length > 0 && url[url.length - 1] === '/') url = url.slice(0, -1);

    this.serverUrl = url;
    this.roomname = roomname;
    this.doc = doc;
    this.params = opts.params ?? {};
    this.awareness = opts.awareness ?? new Awareness(doc);
    this.shouldConnect = opts.connect ?? true;

    const tokenProvider = opts.tokenProvider;

    this.hp = new HocuspocusProvider({
      url,
      name: roomname,
      document: doc,
      awareness: this.awareness,
      // A token function lets Hocuspocus re-read the latest auth token on every
      // (re)connect. Reads `this.params.token` LIVE (not a captured snapshot) so
      // wsTokenRefresher's `provider.params.token = …` rotation is picked up on
      // the next reconnect, exactly as y-websocket behaved.
      token: () => (tokenProvider ? tokenProvider() : this.params.token) ?? '',
      // Deferred connect. The `url` shorthand makes HocuspocusProvider own its
      // socket (manageSocket=true), so its connect()/disconnect() work — but the
      // dial-immediately flag is the internal HocuspocusProviderWebsocket's
      // `autoConnect`, NOT a provider-config field. Forward it via spread (the
      // provider config type does not declare it) so `connect:false` actually
      // defers; omitting it defaults to autoConnect=true, mirroring y-websocket.
      ...(this.shouldConnect ? {} : { autoConnect: false }),
      ...(opts.WebSocketPolyfill ? { WebSocketPolyfill: opts.WebSocketPolyfill } : {}),
      onStatus: ({ status }) => {
        this._status = status;
        this.dispatch('status', { status });
      },
      onSynced: ({ state }) => {
        this.dispatch('sync', state);
        this.dispatch('synced', state);
      },
      onClose: ({ event }) => {
        this.dispatch('connection-close', event);
      },
      onDisconnect: ({ event }) => {
        this.dispatch('connection-error', event);
      },
    });
  }

  // ── WebsocketProvider-compatible status getters ──────────────────────────

  get wsconnected(): boolean {
    return this._status === WebSocketStatus.Connected;
  }

  get wsconnecting(): boolean {
    return this._status === WebSocketStatus.Connecting;
  }

  get bcconnected(): boolean {
    return false;
  }

  get synced(): boolean {
    return this.hp.isSynced;
  }

  set synced(state: boolean) {
    this.hp.synced = state;
  }

  /**
   * Raw underlying WebSocket, if Hocuspocus currently holds one. Exposed only
   * for the observability layer; returns null when unavailable. Never assume
   * presence — Hocuspocus owns the socket lifecycle.
   */
  get ws(): unknown {
    const socketProvider = (this.hp as unknown as {
      configuration?: { websocketProvider?: { webSocket?: unknown } };
    }).configuration?.websocketProvider;
    return socketProvider?.webSocket ?? null;
  }

  /** The wrapped HocuspocusProvider (escape hatch for HP-native APIs). */
  get hocuspocusProvider(): HocuspocusProvider {
    return this.hp;
  }

  // ── Lifecycle ────────────────────────────────────────────────────────────

  connect(): void {
    this.shouldConnect = true;
    // Reflect intent synchronously (matches y-websocket); the real status
    // arrives via the onStatus callback shortly after.
    this._status = WebSocketStatus.Connecting;
    void this.hp.connect();
  }

  disconnect(): void {
    this.shouldConnect = false;
    // y-websocket flips wsconnected synchronously on disconnect(); mirror that
    // so callers reading wsconnected immediately after see the correct value.
    this._status = WebSocketStatus.Disconnected;
    this.hp.disconnect();
  }

  destroy(): void {
    if (this._destroyed) return;
    this._destroyed = true;
    this.listeners.clear();
    try { this.hp.destroy(); } catch { /* best-effort */ }
  }

  // ── Minimal EventEmitter surface (y-websocket compatible) ────────────────

  on(event: string, fn: Listener): void {
    let set = this.listeners.get(event);
    if (!set) { set = new Set(); this.listeners.set(event, set); }
    set.add(fn);
  }

  once(event: string, fn: Listener): void {
    const wrapper: Listener = (...args) => {
      this.off(event, wrapper);
      fn(...args);
    };
    this.on(event, wrapper);
  }

  off(event: string, fn?: Listener): void {
    if (!fn) { this.listeners.delete(event); return; }
    this.listeners.get(event)?.delete(fn);
  }

  private dispatch(event: string, ...args: unknown[]): void {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const fn of [...set]) {
      try { fn(...args); } catch (err) {
        console.error(`[HPWebsocketProvider] listener for "${event}" threw:`, err);
      }
    }
  }
}
